import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';

import { MapFilterService, ResultFilterCompositionDto } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class MapFilterCompositionService {
    public readonly compositionControl: WritableSignal<FormControl<string>> = signal(
        new FormControl('', { nonNullable: true }),
    );
    public readonly failCompositionResult: WritableSignal<string[] | null> = signal(null);
    public readonly filterAttributeCount: WritableSignal<number> = signal<number>(0);
    public readonly isCompositionCheckedAndValid: WritableSignal<boolean | null> = signal(null);
    public readonly isValidationInProgress: WritableSignal<boolean> = signal(false);
    public readonly lastCheckedValue = signal<string | null>(null);

    private readonly sdkMapFilterService = inject(MapFilterService);

    public constructor() {
        effect(() => {
            const subscriptionInputChange: Subscription = this.compositionControl().valueChanges.subscribe(() => {
                if (
                    this.compositionControl().value !== this.lastCheckedValue() &&
                    this.isCompositionCheckedAndValid()
                ) {
                    this.resetValidation();
                }
            });

            return (): void => subscriptionInputChange.unsubscribe();
        });
    }

    public attachCompositionControl(control: FormControl<string>): void {
        this.compositionControl.set(control);
        this.lastCheckedValue.set(null);
        this.resetValidation();
    }

    public async checkComposition(): Promise<void> {
        try {
            this.isValidationInProgress.set(true);
            this.failCompositionResult.set(null);
            this.compositionControl().markAsPending();

            const checkValidation: ResultFilterCompositionDto = await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerCheckFilterComposition({
                    filterCompositionDto: {
                        composition: this.compositionControl()!.value!,
                        filterAttributeCount: this.filterAttributeCount(),
                    },
                }),
            );
            this.isCompositionCheckedAndValid.set(checkValidation.isValid);

            if (checkValidation.errors?.length) {
                this.failCompositionResult.set(checkValidation.errors);

                return;
            }

            this.failCompositionResult.set([]);
            this.lastCheckedValue.set(this.compositionControl().value);
        } catch {
            this.isCompositionCheckedAndValid.set(false);
            this.compositionControl().setErrors({ compositionCheckFailed: true });
        } finally {
            this.compositionControl().markAsDirty();
            this.compositionControl().markAsTouched();
            this.compositionControl().updateValueAndValidity({ emitEvent: true });
            this.isValidationInProgress.set(false);
        }
    }

    public async checkCompositionIfNeeded(): Promise<void> {
        if (this.isCompositionCheckedAndValid()) {
            return;
        }

        if (!this.compositionControl().value) {
            this.failCompositionResult.set(['emptyExpression']);

            return;
        }

        await this.checkComposition();
    }

    public resetValidation(): void {
        this.isValidationInProgress.set(false);
        this.isCompositionCheckedAndValid.set(null);
        this.failCompositionResult.set(null);
        this.compositionControl().setErrors(null);
        this.compositionControl().updateValueAndValidity({ emitEvent: true, onlySelf: true });
    }

    public setFilterAttributeCount(count: number): void {
        this.filterAttributeCount.set(count);
        this.resetValidation();
    }
}
